import { useCallback, useEffect, useRef, useState } from "react";
import {
  ADMIN_COACH_ASSIGNMENT_REASONS,
  COACH_ASSIGN_LABEL,
  COACH_ASSIGNMENT_REASON_LABELS,
  COACH_CLAIM_LABEL,
  type CoachAssignmentHistory,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  assignCoach,
  claimCoach,
  fetchCoachAssignments,
} from "@/lib/coach-assignment-api";
import { errorText } from "@/lib/error-text";
import { useReadOnly } from "./SupportView";

// TES-1218 (Ö-407) — the patient's primary coach: "Tilldela mig" for a coach,
// "Tilldela coach" for an admin, and the change log on the patient page.

const BTN =
  "rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50";
const BTN_PRIMARY =
  "rounded-md bg-brand px-md py-sm text-sm text-white hover:bg-brand/90 disabled:opacity-50";
const FIELD =
  "rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm text-textPrimary focus:border-brand focus:outline-none";

type AdminReason = (typeof ADMIN_COACH_ASSIGNMENT_REASONS)[number];

/** Admin — base role or the TES-1089 grant (a coach + admin). */
export function useIsAdmin(): boolean {
  const { claims } = useAuth();
  return claims?.role === "admin" || Boolean(claims?.roles?.includes("admin"));
}

function messageOf(e: unknown): string {
  return e instanceof Error ? errorText(e.message) : String(e);
}

/** "Tilldela mig" — the coach takes the patient from the shared case. */
export function CoachClaimButton({
  patientId,
  onAssigned,
  onUnauthorized,
}: {
  patientId: string;
  onAssigned: () => void;
  onUnauthorized?: () => void;
}) {
  // TES-1040 — a support view is not a coach: she cannot take a patient
  // there. "Tilldela coach" stays available in it, deliberately: assigning
  // is the admin's own action, not a clinician's write done on her behalf.
  const readOnly = useReadOnly();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (readOnly) return null;
  async function claim() {
    setBusy(true);
    setError(null);
    try {
      await claimCoach(patientId);
      onAssigned();
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized?.();
      else setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button
        type="button"
        className={BTN_PRIMARY}
        disabled={busy}
        onClick={() => void claim()}
      >
        {COACH_CLAIM_LABEL}
      </button>
      {error ? <span className="text-sm text-danger">{error}</span> : null}
    </>
  );
}

/**
 * "Tilldela coach" — the admin picks the coach and the reason. Loads the
 * active coaches from the history read, so the picker needs no route of its
 * own.
 */
export function CoachAssignForm({
  patientId,
  currentCoachId,
  onAssigned,
  onCancel,
  onUnauthorized,
}: {
  patientId: string;
  currentCoachId?: string | null;
  onAssigned: () => void;
  onCancel: () => void;
  onUnauthorized?: () => void;
}) {
  const [coaches, setCoaches] = useState<CoachAssignmentHistory["coaches"]>([]);
  const [coachId, setCoachId] = useState("");
  const [reasonCode, setReasonCode] = useState<AdminReason>("admin_assigned");
  const [reasonText, setReasonText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The parent passes a fresh arrow every render; reading it through a ref
  // keeps the load from re-running on each of them.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let cancelled = false;
    fetchCoachAssignments(patientId)
      .then((h) => {
        if (!cancelled) setCoaches(h.coaches);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current?.();
        else setError(messageOf(e));
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const choices = coaches.filter((c) => c.id !== currentCoachId);
  const ready =
    coachId !== "" && (reasonCode !== "other" || reasonText.trim() !== "");

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await assignCoach(patientId, {
        coachId,
        reasonCode,
        ...(reasonText.trim() ? { reasonText: reasonText.trim() } : {}),
      });
      onAssigned();
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized?.();
      else setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-sm">
      <select
        aria-label="Coach"
        className={FIELD}
        value={coachId}
        onChange={(e) => setCoachId(e.target.value)}
      >
        <option value="">Välj coach</option>
        {choices.map((c) => (
          <option key={c.id} value={c.id}>
            {c.displayName}
          </option>
        ))}
      </select>
      <select
        aria-label="Orsak"
        className={FIELD}
        value={reasonCode}
        onChange={(e) => setReasonCode(e.target.value as AdminReason)}
      >
        {ADMIN_COACH_ASSIGNMENT_REASONS.map((r) => (
          <option key={r} value={r}>
            {COACH_ASSIGNMENT_REASON_LABELS[r]}
          </option>
        ))}
      </select>
      {reasonCode === "other" ? (
        <input
          aria-label="Orsak"
          className={FIELD}
          value={reasonText}
          maxLength={2000}
          onChange={(e) => setReasonText(e.target.value)}
        />
      ) : null}
      <button
        type="button"
        className={BTN_PRIMARY}
        disabled={busy || !ready}
        onClick={() => void submit()}
      >
        {COACH_ASSIGN_LABEL}
      </button>
      <button type="button" className={BTN} onClick={onCancel}>
        Avbryt
      </button>
      {error ? <span className="text-sm text-danger">{error}</span> : null}
    </div>
  );
}

/** The actions on the shared "Ny patient saknar coach" card. */
export function UnassignedCoachActions({
  patientId,
  canClaim,
  onAssigned,
  onUnauthorized,
}: {
  patientId: string;
  canClaim: boolean;
  onAssigned: () => void;
  onUnauthorized?: () => void;
}) {
  const admin = useIsAdmin();
  const [assigning, setAssigning] = useState(false);
  if (assigning) {
    return (
      <CoachAssignForm
        patientId={patientId}
        onAssigned={onAssigned}
        onCancel={() => setAssigning(false)}
        onUnauthorized={onUnauthorized}
      />
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-sm">
      {canClaim ? (
        <CoachClaimButton
          patientId={patientId}
          onAssigned={onAssigned}
          onUnauthorized={onUnauthorized}
        />
      ) : null}
      {admin ? (
        <button
          type="button"
          className={BTN}
          onClick={() => setAssigning(true)}
        >
          {COACH_ASSIGN_LABEL}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The patient page's coach section: the current primary coach, the change
 * log (from, to, when, why) and, for an admin, "Tilldela coach".
 */
export function CoachAssignmentPanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized?: () => void;
}) {
  const admin = useIsAdmin();
  const [history, setHistory] = useState<CoachAssignmentHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  const load = useCallback(() => {
    fetchCoachAssignments(patientId)
      .then((h) => {
        setHistory(h);
        setError(null);
      })
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current?.();
        else setError(messageOf(e));
      });
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="text-sm text-danger">{error}</p>;
  if (!history) return null;

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex flex-wrap items-center gap-sm">
        <span className="text-sm text-textPrimary">
          {history.current?.displayName ?? "Ingen coach tilldelad"}
        </span>
        {admin && !assigning ? (
          <button
            type="button"
            className={BTN}
            onClick={() => setAssigning(true)}
          >
            {COACH_ASSIGN_LABEL}
          </button>
        ) : null}
      </div>
      {assigning ? (
        <CoachAssignForm
          patientId={patientId}
          currentCoachId={history.current?.id ?? null}
          onAssigned={() => {
            setAssigning(false);
            load();
          }}
          onCancel={() => setAssigning(false)}
          onUnauthorized={onUnauthorized}
        />
      ) : null}
      {history.entries.length > 0 ? (
        <ul className="flex flex-col gap-xs text-sm text-textSecondary">
          {history.entries.map((e) => (
            <li key={e.id}>
              <span className="text-textPrimary">
                {e.fromCoach ? `${e.fromCoach.displayName} → ` : ""}
                {e.toCoach.displayName}
              </span>{" "}
              · {e.assignedAt.slice(0, 10)} ·{" "}
              {COACH_ASSIGNMENT_REASON_LABELS[e.reasonCode]}
              {e.reasonText ? `: ${e.reasonText}` : ""}
              {e.assignedBy ? ` · ${e.assignedBy.displayName}` : ""}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
